import {
  RSI_OVERBOUGHT,
  RSI_OVERSOLD,
  timeframeShort,
  type RsiAnalysis,
  type Timeframe,
} from '@pipyar/core';
import { cn } from '@/lib/utils';

/*
 * Geometry.
 *
 * A half circle rather than the 240-degree sweep a car uses. The dashboard
 * card has to show eight of these without scrolling, and a semicircle is the
 * shape that buys the most dial for the least height — and with no needle, it
 * leaves the whole middle free for the number.
 *
 * Angles follow SVG convention — 0 is east, y grows downward — so the dial
 * runs 180 (left, RSI 0) through 270 (top, RSI 50) to 360 (right, RSI 100).
 */
const START = 180;
const SWEEP = 180;
const CX = 60;
const CY = 58;
const R = 44;

const angleFor = (value: number) => START + (Math.min(100, Math.max(0, value)) / 100) * SWEEP;

function polar(r: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

/** An arc along the dial between two RSI values. */
function band(from: number, to: number, r: number): string {
  const a0 = angleFor(from);
  const a1 = angleFor(to);
  const p0 = polar(r, a0);
  const p1 = polar(r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
}

/**
 * The colour a zone argues for.
 *
 * Oversold is drawn in the bull colour and overbought in the bear colour,
 * which is the opposite of the number going up. Deliberate, and it matches the
 * rest of the module: RSI at 20 is an argument for a bounce, not for more
 * selling. The dial reads "what this suggests", not "which way it moved".
 */
const ZONE_COLOR: Record<RsiAnalysis['zone'], string> = {
  oversold: 'var(--bull)',
  overbought: 'var(--bear)',
  neutral: 'var(--muted-foreground)',
};

export interface RsiGaugeProps {
  row: RsiAnalysis;
  className?: string;
}

/**
 * One timeframe's RSI as a dial.
 *
 * No needle: the reading is the filled length of the arc, and its end cap
 * marks the exact value. That is what frees the middle for the number, and on
 * a tile this small a needle was mostly hub anyway.
 *
 * The oversold and overbought bands are always drawn, so the number never has
 * to be remembered against its thresholds — 68 and 72 are four apart and on
 * opposite sides of a decision. Only the band the reading sits in is at full
 * strength, so the dial states its verdict before a digit is read.
 *
 * Everything including the readout lives inside the SVG, so the whole gauge is
 * one box that scales to whatever cell it is given. That is what lets the card
 * fit eight of them by height instead of guessing pixel sizes.
 *
 * The dial runs 0 on the left and 100 on the right, which is how an RSI gauge
 * is read everywhere.
 */
export function RsiGauge({ row, className }: RsiGaugeProps) {
  const value = row.value;
  const known = value !== null;
  const accent = ZONE_COLOR[row.zone];

  // The reading: an arc from 0 to the value, with its end cap as the marker.
  const reading = known ? band(0, Math.max(0.8, value), R) : null;
  const tip = known ? polar(R, angleFor(value)) : null;

  return (
    <svg
      viewBox="0 0 120 78"
      preserveAspectRatio="xMidYMid meet"
      className={cn('h-full max-h-full w-full min-w-0', className)}
      role="img"
      aria-label={`RSI ${timeframeShort(row.timeframe as Timeframe)} ${known ? value.toFixed(0) : 'unavailable'}`}
    >
      {/* the track */}
      <path d={band(0, 100, R)} fill="none" stroke="var(--muted)" strokeWidth={10} strokeLinecap="round" />

      {/* the zones; opacity carries which one is live */}
      <path
        d={band(0, RSI_OVERSOLD, R)}
        fill="none"
        stroke="var(--bull)"
        strokeWidth={10}
        opacity={row.zone === 'oversold' ? 0.75 : 0.16}
      />
      <path
        d={band(RSI_OVERBOUGHT, 100, R)}
        fill="none"
        stroke="var(--bear)"
        strokeWidth={10}
        opacity={row.zone === 'overbought' ? 0.75 : 0.16}
      />

      {/* graduations: every ten, with 30/50/70 called out */}
      {Array.from({ length: 11 }, (_, i) => i * 10).map((v) => {
        const key = v === 30 || v === 50 || v === 70;
        const outer = polar(R - 7, angleFor(v));
        const inner = polar(R - (key ? 12.5 : 10), angleFor(v));
        return (
          <line
            key={v}
            x1={outer.x}
            y1={outer.y}
            x2={inner.x}
            y2={inner.y}
            stroke={key ? 'var(--foreground)' : 'var(--muted-foreground)'}
            strokeWidth={key ? 1.5 : 0.9}
            opacity={key ? 0.5 : 0.3}
            strokeLinecap="round"
          />
        );
      })}

      {/* the reading, over the track */}
      {reading && (
        <path
          d={reading}
          fill="none"
          stroke={accent}
          strokeWidth={6.5}
          strokeLinecap="round"
          style={{ transition: 'stroke 400ms ease' }}
        />
      )}
      {/* its end cap, so the exact value is pinned rather than implied */}
      {tip && (
        <>
          <circle cx={tip.x} cy={tip.y} r={5} fill="var(--background)" />
          <circle cx={tip.x} cy={tip.y} r={3.4} fill={accent} style={{ transition: 'fill 400ms ease' }} />
        </>
      )}

      {/* the number, in the middle the needle used to occupy */}
      <text
        x={CX}
        y={53}
        textAnchor="middle"
        fontSize={27}
        fontWeight={650}
        fill={known ? accent : 'var(--muted-foreground)'}
        style={{ fontVariantNumeric: 'tabular-nums', transition: 'fill 400ms ease' }}
      >
        {known ? value.toFixed(0) : '—'}
      </text>

      {/* which timeframe this is, below the dial */}
      <text
        x={CX}
        y={74}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill="var(--muted-foreground)"
      >
        {timeframeShort(row.timeframe as Timeframe)}
      </text>
      {row.strong && (
        /* A zone backed by a matching divergence — the stronger read. */
        <circle cx={113} cy={70} r={3} fill={accent} />
      )}
    </svg>
  );
}
